import { cn } from "@babackapp/customer-ui/lib/utils";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

export type ReasonOption = {
  id: string;
  label: string;
  gapBefore?: number;
};

type ReasonOptionSelectorProps = {
  value: string;
  options: ReasonOption[];
  onChange: (value: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
};

const ITEM_H = 40;
const DEFAULT_GAP = 4;
const SPRING = { type: "spring", stiffness: 300, damping: 30 } as const;

export function ReasonOptionSelector({ value, options, onChange, onOpenChange }: ReasonOptionSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    onOpenChange?.(isExpanded);
  }, [isExpanded, onOpenChange]);

  // y offset of each item when the list is expanded
  const expandedY = options.map((_, n) =>
    options.slice(0, n).reduce(
      (acc, _, i) => acc + ITEM_H + (options[i + 1]?.gapBefore ?? DEFAULT_GAP),
      0,
    ),
  );

  const totalHeight =
    (expandedY.at(-1) ?? 0) + ITEM_H + DEFAULT_GAP;

  const handleClick = (id: string) => {
    if (isExpanded) {
      onChange(id);
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  };

  return (
    <motion.div
      className="relative overflow-hidden"
      animate={{ height: isExpanded ? totalHeight : ITEM_H }}
      transition={SPRING}
    >
      {options.map((option, index) => {
        const isSelected = option.id === value;
        return (
          <motion.button
            key={option.id}
            type="button"
            onClick={() => handleClick(option.id)}
            className={cn(
              "absolute inset-x-0 top-0 flex h-10 w-full cursor-pointer items-center rounded-lg border border-border/80 bg-background px-3 text-sm text-left hover:bg-accent hover:text-accent-foreground",
              isSelected && !isExpanded ? "font-medium" : "font-normal",
              isSelected ? "z-10" : "z-0",
            )}
            animate={{
              y: isExpanded ? expandedY[index] : 0,
              opacity: isExpanded || isSelected ? 1 : 0,
            }}
            transition={SPRING}
          >
            {option.label}
          </motion.button>
        );
      })}
    </motion.div>
  );
}
